import React from 'react';
import { ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

// Section blocks: { p }, { h3 }, { ul: [text | [boldLabel, text]] }, { contact: true }
const CONTENT = {
  en: {
    back: 'Back',
    title: 'Privacy Policy',
    updated: 'Last updated: January 22, 2026',
    footer: '© 2026 SocraQuest. All rights reserved.',
    contact: { email: 'Email', website: 'Website' },
    sections: [
      ['1. Introduction', [
        { p: 'Welcome to SocraQuest ("we," "our," or "us"). We are committed to protecting your personal information and your right to privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our mobile application and website (collectively, the "Service").' },
        { p: 'Please read this privacy policy carefully. If you do not agree with the terms of this privacy policy, please do not access the Service.' },
      ]],
      ['2. Information We Collect', [
        { h3: '2.1 Personal Information' },
        { p: 'We may collect the following personal information:' },
        { ul: [
          'Email address (for account registration and communication)',
          'Username/display name',
          'Profile information (first name, last name - optional)',
          'Payment information (bank account details for reward payouts - optional)',
        ] },
        { h3: '2.2 Usage Data' },
        { p: 'We automatically collect certain information when you use our Service:' },
        { ul: [
          'Assessment participation and scores',
          'App usage statistics',
          'Device information (device type, operating system)',
          'Log data (access times, pages viewed)',
        ] },
      ]],
      ['3. How We Use Your Information', [
        { p: 'We use the collected information for various purposes:' },
        { ul: [
          'To provide and maintain our Service',
          'To manage your account and provide customer support',
          'To track your learning progress and display rankings',
          'To process reward payouts (if applicable)',
          'To send you notifications about your account or the Service',
          'To improve our Service and develop new features',
          'To detect and prevent fraud or abuse',
          'To comply with legal obligations',
        ] },
      ]],
      ['4. Data Sharing and Disclosure', [
        { p: 'We may share your information in the following situations:' },
        { ul: [
          ['Public Leaderboards:', 'Your username and scores may be displayed on public leaderboards'],
          ['Service Providers:', 'We may share data with third-party vendors who assist in operating our Service'],
          ['Legal Requirements:', 'We may disclose information if required by law or legal process'],
          ['Business Transfers:', 'In case of merger, acquisition, or sale of assets'],
        ] },
        { p: 'We do not sell your personal information to third parties.' },
      ]],
      ['5. Data Security', [
        { p: 'We implement appropriate technical and organizational security measures to protect your personal information. However, no method of transmission over the Internet or electronic storage is 100% secure. While we strive to use commercially acceptable means to protect your personal information, we cannot guarantee its absolute security.' },
      ]],
      ['6. Data Retention', [
        { p: 'We retain your personal information only for as long as necessary to fulfill the purposes outlined in this Privacy Policy, unless a longer retention period is required or permitted by law. When you delete your account, we will delete or anonymize your personal information within 30 days, except where we are required to retain it for legal purposes.' },
      ]],
      ['7. Your Rights', [
        { p: 'Depending on your location, you may have the following rights:' },
        { ul: [
          ['Access:', 'Request access to your personal data'],
          ['Correction:', 'Request correction of inaccurate data'],
          ['Deletion:', 'Request deletion of your personal data'],
          ['Portability:', 'Request a copy of your data in a portable format'],
          ['Objection:', 'Object to processing of your personal data'],
          ['Withdrawal:', 'Withdraw consent at any time'],
        ] },
        { p: 'To exercise these rights, please contact us at the email address provided below.' },
      ]],
      ["8. Children's Privacy", [
        { p: 'Our Service is not intended for children under 13 years of age. We do not knowingly collect personal information from children under 13. If you are a parent or guardian and believe your child has provided us with personal information, please contact us immediately. If we discover that we have collected personal information from a child under 13, we will take steps to delete that information.' },
      ]],
      ['9. Third-Party Services', [
        { p: 'Our Service may contain links to or integrate with third-party services:' },
        { ul: [
          ['Analytics:', 'We may use analytics tools to understand app usage'],
          ['Advertising:', 'We may display advertisements from third-party networks'],
          ['AI Services:', 'We use AI to generate assessment images and learning explanations (no personal data shared)'],
        ] },
        { p: 'These third-party services have their own privacy policies, and we encourage you to review them.' },
      ]],
      ['10. International Data Transfers', [
        { p: 'Your information may be transferred to and processed in countries other than your country of residence. These countries may have different data protection laws. By using our Service, you consent to the transfer of your information to these countries.' },
      ]],
      ['11. Changes to This Privacy Policy', [
        { p: 'We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last updated" date. You are advised to review this Privacy Policy periodically for any changes.' },
      ]],
      ['12. Contact Us', [
        { p: 'If you have any questions about this Privacy Policy or our data practices, please contact us:' },
        { contact: true },
      ]],
    ],
  },
  sk: {
    back: 'Späť',
    title: 'Zásady ochrany osobných údajov',
    updated: 'Posledná aktualizácia: 22. januára 2026',
    footer: '© 2026 SocraQuest. Všetky práva vyhradené.',
    contact: { email: 'E-mail', website: 'Webová stránka' },
    sections: [
      ['1. Úvod', [
        { p: 'Vitajte v SocraQuest („my“, „nás“ alebo „náš“). Záleží nám na ochrane Vašich osobných údajov a Vášho práva na súkromie. Tieto Zásady ochrany osobných údajov vysvetľujú, ako zhromažďujeme, používame, sprístupňujeme a chránime Vaše údaje, keď používate našu mobilnú aplikáciu a webovú stránku (spolu ďalej „Služba“).' },
        { p: 'Prečítajte si, prosím, tieto zásady pozorne. Ak s nimi nesúhlasíte, Službu, prosím, nepoužívajte.' },
      ]],
      ['2. Aké údaje zhromažďujeme', [
        { h3: '2.1 Osobné údaje' },
        { p: 'Môžeme zhromažďovať tieto osobné údaje:' },
        { ul: [
          'E-mailová adresa (na registráciu účtu a komunikáciu)',
          'Používateľské meno / prezývka',
          'Údaje profilu (meno, priezvisko – nepovinné)',
          'Platobné údaje (číslo bankového účtu na vyplatenie odmien – nepovinné)',
        ] },
        { h3: '2.2 Údaje o používaní' },
        { p: 'Pri používaní Služby automaticky zhromažďujeme niektoré údaje:' },
        { ul: [
          'Účasť na hodnoteniach a dosiahnuté skóre',
          'Štatistiky používania aplikácie',
          'Údaje o zariadení (typ zariadenia, operačný systém)',
          'Záznamy (čas prístupu, zobrazené stránky)',
        ] },
      ]],
      ['3. Ako Vaše údaje používame', [
        { p: 'Zhromaždené údaje používame na tieto účely:' },
        { ul: [
          'na poskytovanie a prevádzku Služby,',
          'na správu Vášho účtu a zákaznícku podporu,',
          'na sledovanie Vášho pokroku v učení a zobrazovanie rebríčkov,',
          'na vyplácanie odmien (ak sa Vás týka),',
          'na zasielanie upozornení o Vašom účte alebo Službe,',
          'na zlepšovanie Služby a vývoj nových funkcií,',
          'na odhaľovanie a predchádzanie podvodom alebo zneužitiu,',
          'na plnenie zákonných povinností.',
        ] },
      ]],
      ['4. Zdieľanie a sprístupnenie údajov', [
        { p: 'Vaše údaje môžeme zdieľať v týchto prípadoch:' },
        { ul: [
          ['Verejné rebríčky:', 'Vaše používateľské meno a skóre sa môžu zobrazovať vo verejných rebríčkoch'],
          ['Poskytovatelia služieb:', 'Údaje môžeme zdieľať s externými dodávateľmi, ktorí nám pomáhajú prevádzkovať Službu'],
          ['Zákonné požiadavky:', 'Údaje môžeme sprístupniť, ak to vyžaduje zákon alebo súdne či úradné konanie'],
          ['Prevod podniku:', 'V prípade zlúčenia, akvizície alebo predaja majetku'],
        ] },
        { p: 'Vaše osobné údaje nepredávame tretím stranám.' },
      ]],
      ['5. Bezpečnosť údajov', [
        { p: 'Na ochranu Vašich osobných údajov používame primerané technické a organizačné bezpečnostné opatrenia. Žiadny spôsob prenosu cez internet ani elektronického uchovávania však nie je stopercentne bezpečný. Hoci sa snažíme Vaše osobné údaje chrániť komerčne primeranými prostriedkami, nemôžeme zaručiť ich absolútnu bezpečnosť.' },
      ]],
      ['6. Uchovávanie údajov', [
        { p: 'Vaše osobné údaje uchovávame len tak dlho, ako je potrebné na účely uvedené v týchto zásadách, pokiaľ zákon nevyžaduje alebo nepovoľuje dlhšiu dobu. Ak svoj účet vymažete, Vaše osobné údaje do 30 dní vymažeme alebo anonymizujeme, okrem prípadov, keď ich musíme uchovať zo zákonných dôvodov.' },
      ]],
      ['7. Vaše práva', [
        { p: 'V závislosti od miesta Vášho bydliska môžete mať tieto práva:' },
        { ul: [
          ['Prístup:', 'požiadať o prístup k Vašim osobným údajom'],
          ['Oprava:', 'požiadať o opravu nesprávnych údajov'],
          ['Vymazanie:', 'požiadať o vymazanie Vašich osobných údajov'],
          ['Prenosnosť:', 'požiadať o kópiu Vašich údajov v prenosnom formáte'],
          ['Námietka:', 'namietať proti spracúvaniu Vašich osobných údajov'],
          ['Odvolanie súhlasu:', 'kedykoľvek odvolať svoj súhlas'],
        ] },
        { p: 'Ak chcete tieto práva uplatniť, kontaktujte nás na e-mailovej adrese uvedenej nižšie.' },
      ]],
      ['8. Ochrana súkromia detí', [
        { p: 'Služba nie je určená deťom mladším ako 13 rokov. Vedome nezhromažďujeme osobné údaje detí mladších ako 13 rokov. Ak ste rodič alebo zákonný zástupca a domnievate sa, že nám Vaše dieťa poskytlo osobné údaje, okamžite nás kontaktujte. Ak zistíme, že sme získali osobné údaje dieťaťa mladšieho ako 13 rokov, podnikneme kroky na ich vymazanie.' },
      ]],
      ['9. Služby tretích strán', [
        { p: 'Služba môže obsahovať odkazy na služby tretích strán alebo s nimi spolupracovať:' },
        { ul: [
          ['Analytika:', 'môžeme používať analytické nástroje na pochopenie používania aplikácie'],
          ['Reklama:', 'môžeme zobrazovať reklamy z reklamných sietí tretích strán'],
          ['Služby umelej inteligencie:', 'umelú inteligenciu používame na tvorbu obrázkov k hodnoteniam a na vysvetlenia (nezdieľame pritom žiadne osobné údaje)'],
        ] },
        { p: 'Tieto služby tretích strán majú vlastné zásady ochrany osobných údajov, odporúčame Vám ich prečítať.' },
      ]],
      ['10. Medzinárodný prenos údajov', [
        { p: 'Vaše údaje môžu byť prenesené a spracúvané v iných krajinách, než je krajina Vášho bydliska. V týchto krajinách môžu platiť iné predpisy o ochrane údajov. Používaním Služby súhlasíte s prenosom Vašich údajov do týchto krajín.' },
      ]],
      ['11. Zmeny týchto zásad', [
        { p: 'Tieto Zásady ochrany osobných údajov môžeme z času na čas aktualizovať. O zmenách Vás budeme informovať zverejnením nových zásad na tejto stránke a aktualizáciou dátumu „Posledná aktualizácia“. Odporúčame Vám tieto zásady pravidelne kontrolovať.' },
      ]],
      ['12. Kontakt', [
        { p: 'Ak máte otázky k týmto zásadám alebo k spracúvaniu Vašich údajov, kontaktujte nás:' },
        { contact: true },
      ]],
    ],
  },
};

function Block({ block, c }) {
  if (block.h3) return <h3 className="text-lg font-medium text-slate-700 mb-3">{block.h3}</h3>;
  if (block.p) return <p className="text-slate-600 mb-4">{block.p}</p>;
  if (block.ul) {
    return (
      <ul className="list-disc list-inside text-slate-600 mb-4 space-y-2">
        {block.ul.map((item) => (Array.isArray(item)
          ? <li key={item[0]}><strong>{item[0]}</strong> {item[1]}</li>
          : <li key={item}>{item}</li>))}
      </ul>
    );
  }
  if (block.contact) {
    return (
      <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
        <p className="text-slate-700"><strong>SocraQuest</strong></p>
        <p className="text-slate-600">{c.contact.email}: privacy@socraquest.sk</p>
        <p className="text-slate-600">{c.contact.website}: https://socraquest.sk</p>
      </div>
    );
  }
  return null;
}

export default function PrivacyPolicyPage() {
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const lang = (i18n.language || 'en').toLowerCase().startsWith('sk') ? 'sk' : 'en';
  const c = CONTENT[lang];

  const goBack = () => {
    if (window.history.length > 1) navigate(-1);
    else navigate('/');
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center">
          <button onClick={goBack} className="flex items-center text-teal-600 hover:text-teal-700 mr-4" data-testid="back-btn">
            <ArrowLeft className="w-5 h-5 mr-1" />
            {c.back}
          </button>
          <h1 className="text-xl font-bold text-slate-800">SocraQuest</h1>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8">
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 sm:p-8" data-testid="privacy-policy">
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-800 mb-2">{c.title}</h1>
          <p className="text-slate-500 mb-8">{c.updated}</p>

          <div className="prose prose-slate max-w-none">
            {c.sections.map(([heading, blocks]) => (
              <section key={heading} className="mb-8">
                <h2 className="text-xl font-semibold text-slate-800 mb-4">{heading}</h2>
                {blocks.map((block, i) => <Block key={i} block={block} c={c} />)}
              </section>
            ))}
          </div>
        </div>
      </main>

      <footer className="bg-white border-t border-slate-200 mt-8">
        <div className="max-w-4xl mx-auto px-4 py-6 text-center text-slate-500 text-sm">
          {c.footer}
        </div>
      </footer>
    </div>
  );
}
